import type { ActionContext, ActionTree } from 'vuex'
import type { CustomCalendarEntry, CustomCalendarEntryState } from './types'
import type { RootState } from '@/store/modules/types'
import { getContext } from '@/utils/general'
import { requestApi } from '@/utils/requestApi'

export interface AddCustomCalendarEntry {
  title: string
  entryType: string
  startDate: string
  description?: string
  endDate?: string
  color?: string
  eventOids?: number[]
  timeZone?: string
}

export interface UpdateCustomCalendarEntry {
  oid: number
  title?: string
  description?: string | null
  startDate?: string
  endDate?: string | null
  color?: string
  eventOids?: number[]
  timeZone?: string
}

export interface CustomCalendarEntryActions extends ActionTree<CustomCalendarEntryState, RootState> {
  FETCH_ENTRIES(context: ActionContext<CustomCalendarEntryState, RootState>): Promise<void>
  CREATE_ENTRY(context: ActionContext<CustomCalendarEntryState, RootState>, payload: AddCustomCalendarEntry): Promise<CustomCalendarEntry | undefined>
  UPDATE_ENTRY(context: ActionContext<CustomCalendarEntryState, RootState>, payload: UpdateCustomCalendarEntry): Promise<CustomCalendarEntry | undefined>
  DELETE_ENTRY(context: ActionContext<CustomCalendarEntryState, RootState>, oid: number): Promise<void>
}

export const customCalendarEntryActions: CustomCalendarEntryActions = {
  async FETCH_ENTRIES({ commit, rootState }) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account
    const { $axios, $arNotification } = getContext()

    commit('SET_IS_FETCHING', true)
    try {
      const { data } = await $axios.get(`/promoter/${promoterOid}/custom-calendar-entry`, {
        params: { $count: true, $orderby: 'start_date asc' },
      })
      commit('SET_ENTRIES', data.rows ?? data)
    }
    catch {
      $arNotification.push({ type: 'error', message: 'Failed to fetch calendar entries' })
    }
    finally {
      commit('SET_IS_FETCHING', false)
    }
  },

  async CREATE_ENTRY({ commit, rootState }, payload) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account

    const entry = await requestApi('createCustomCalendarEntry', {
      body: { ...payload, promoterOid },
    }) as CustomCalendarEntry | undefined

    if (entry) {
      commit('ADD_ENTRY', entry)
    }
    return entry
  },

  async UPDATE_ENTRY({ commit, rootState }, payload) {
    if (!rootState.authModule.account) { return }
    const { oid, ...body } = payload
    const entry = await requestApi('updateCustomCalendarEntry', {
      params: { entryOid: String(oid) },
      body,
    }) as CustomCalendarEntry | undefined

    if (entry) {
      commit('REPLACE_ENTRY', entry)
    }
    return entry
  },

  async DELETE_ENTRY({ commit }, oid) {
    await requestApi('deleteCustomCalendarEntry', {
      params: { entryOid: String(oid) },
    })
    commit('REMOVE_ENTRY', oid)
  },
}

export default customCalendarEntryActions
